/**
 * **ProDoc, as this app hosts it** — Wave 28E (T-2.33; ADR-0055 §6 and §8).
 *
 * For a firm that has moved its documents onto ProDoc, a client's documents
 * panel is drawn by `@fiveox/prodoc-ui` (`routes/Client360/ProDocDocuments.tsx`).
 * The package owns no session and no org (ProDoc ADR-0007): it is handed this
 * app's `session.authed`, the selected org and ProDoc's public API URL, and
 * every request it makes goes out on them. This file answers the two questions
 * the panel has to settle before it can render the package at all:
 *
 * 1. **Has this client's firm moved?** — I-5's `prodoc` on the client read
 *    (`enabled`, `folder_id`). The service decides it (the org's state
 *    `writing` or `moved`; no switch since C28); this file only reads it.
 * 2. **Where is ProDoc?** — `VITE_PRODOC_API_URL` first, else I-5's
 *    `prodoc_api_url` from the service.
 *
 * Either answer missing means **ProDesk's own panel**, exactly as before Wave 28
 * — never an error, because with the flag off nothing about this app changes.
 */

import { useEffect, useState } from 'react'

import { ApiError, api } from './api'
import { type ClientProDoc, clients } from './api/clients'
import { PRODOC_API_URL, plansUrl } from './config'

/** ProDoc's plans — where the uploader's storage-limit refusal links. This app
 *  never calls the store; it links out (CLAUDE.md §7). C27 item 12 (UAT report
 *  #25): the platform website's pricing page, because the store's
 *  `/catalog/<slug>` 404s. */
export const PRODOC_STORE_URL = plansUrl('prodoc')

/** The client's key on every ProDoc document this app files — ADR-0055 §3.
 *  The id, never the name: a name is a folder in ProDoc and nothing else. */
export function clientSourceRef(clientId: string): string {
  return `client:${clientId}`
}

/** I-5's field, on whichever read the service serves it. */
interface ProDocUrlBody {
  prodoc_api_url?: string | null
}

function cleanUrl(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * The service's answer, kept for the page's life — **but only an answer.**
 * A failed read is not cached: a promise remembers its rejection for ever, and
 * the portal paid for that lesson once already (`stepUpAudience`,
 * `tests/portal-failure.test.tsx`). `undefined` means *not asked yet*.
 */
let discovered: string | null | undefined

/**
 * Where ProDoc's public API is, or `null` when this environment has none.
 *
 * C-4 names *"`GET /api/v1/me` (or ProDesk's existing config/bootstrap
 * endpoint)"*. The staff side has no `GET /api/v1/me`; its bootstrap read is
 * `GET /api/v1/me/access`, so that is asked first, and `GET /api/v1/me` only
 * when the access read does not carry the field at all (a missing key, not a
 * `null`, which is the service saying *none*).
 */
export async function proDocApiUrl(): Promise<string | null> {
  if (PRODOC_API_URL) return PRODOC_API_URL
  if (discovered !== undefined) return discovered
  const access = await api<ProDocUrlBody>('/api/v1/me/access')
  if (access.data.prodoc_api_url !== undefined) {
    discovered = cleanUrl(access.data.prodoc_api_url)
    return discovered
  }
  try {
    const me = await api<ProDocUrlBody>('/api/v1/me')
    discovered = cleanUrl(me.data.prodoc_api_url)
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err
    discovered = null
  }
  return discovered
}

/** For tests: forget the service's answer. */
export function forgetProDocApiUrl(): void {
  discovered = undefined
}

/** Where a client's documents are read from. */
export type ClientDocumentsPlace =
  | { kind: 'reading' }
  /** ProDesk's own panel: the org has not moved, or this environment has no ProDoc. */
  | { kind: 'local' }
  /** ProDoc's package, against `apiUrl`, filing into the client's folder (`null` until its first document). */
  | { kind: 'prodoc'; apiUrl: string; folderId: string | null }

/**
 * Settles where a client's documents panel reads from.
 *
 * `given` is the 360 read's `client.prodoc`. When the service did not put it
 * there (`undefined`), the client read I-5 names is asked instead — so the
 * panel is right whichever of the two reads carries the field.
 */
export function useClientDocumentsPlace(
  clientId: string,
  given: ClientProDoc | null | undefined,
): ClientDocumentsPlace {
  const [place, setPlace] = useState<ClientDocumentsPlace>({ kind: 'reading' })
  const asked = given === undefined
  const enabled = given?.enabled ?? false
  const folderId = given?.folder_id ?? null

  useEffect(() => {
    let live = true
    setPlace({ kind: 'reading' })
    void (async () => {
      let state: ClientProDoc | null = asked ? null : { enabled, folder_id: folderId }
      if (asked) {
        try {
          state = (await clients.get(clientId)).data.prodoc ?? null
        } catch {
          // The 360 read already answered for this client; a failed second read
          // is not a reason to show a broken panel. ProDesk's own list is true
          // for every org, moved or not — its rows stay ProDesk's (ADR-0055 §1).
          state = null
        }
      }
      if (!state?.enabled) {
        if (live) setPlace({ kind: 'local' })
        return
      }
      let apiUrl: string | null = null
      try {
        apiUrl = await proDocApiUrl()
      } catch (err) {
        console.warn('[prodesk] could not read where ProDoc is; showing ProDesk’s own documents', err)
      }
      if (!live) return
      if (!apiUrl) {
        console.warn('[prodesk] this firm has moved to ProDoc, but no ProDoc API URL is configured')
        setPlace({ kind: 'local' })
        return
      }
      setPlace({ kind: 'prodoc', apiUrl, folderId: state.folder_id ?? null })
    })()
    return () => {
      live = false
    }
  }, [clientId, asked, enabled, folderId])

  return place
}
